/**
 * TokenCalculator.jsx
 * ====================
 * 
 * Composant de configuration N Licences × D mois avec affichage du prix.
 * 
 * RÈGLES STRICTES MOA:
 * - ❌ AUCUN calcul de prix côté frontend
 * - ❌ AUCUNE constante de prix
 * - ✅ Le prix unitaire vient de la configuration Tarification admin (via backend)
 * - ✅ Affichage STRICT de la réponse backend (unit_price_monthly, total_price, formula)
 * - ✅ P1 MOA (Jan 2026): Saisie libre dans les champs, validation uniquement à la soumission
 */

import React from 'react';
import { Users, Calendar, Minus, Plus, Loader2, Calculator } from 'lucide-react';

const TokenCalculator = ({
  Licences,
  setLicences,
  durationMonths,
  setDurationMonths,
  pricing,        // Réponse backend - SEULE SOURCE DE VÉRITÉ
  loadingPricing
}) => {
  
  // 🔒 P1 MOA: Increment/decrement handlers - permettent de descendre à 0 mais le + incrémente à partir de 1
  const incrementLicences = () => setLicences(prev => {
    const current = parseInt(prev) || 0;
    return Math.min(current + 1, 10000);
  });
  const decrementLicences = () => setLicences(prev => {
    const current = parseInt(prev) || 0;
    return Math.max(current - 1, 0);  // Permet 0 pendant l'édition
  });
  
  const incrementDuration = () => setDurationMonths(prev => {
    const current = parseInt(prev) || 0;
    return Math.min(current + 1, 36);
  });
  const decrementDuration = () => setDurationMonths(prev => {
    const current = parseInt(prev) || 0;
    return Math.max(current - 1, 0);  // Permet 0 pendant l'édition
  });

  // Format price for display (no calculation, just formatting)
  const formatPrice = (price) => {
    if (price === null || price === undefined) return '—';
    return new Intl.NumberFormat('fr-FR', {
      style: 'currency',
      currency: 'EUR',
      minimumFractionDigits: 2
    }).format(price);
  };

  return (
    <div className="space-y-6 bg-white p-6 rounded-2xl border border-[#E8E5DE] shadow-sm rounded-2xl">
      {/* Row: Licences & Duration */}
      <div className="grid md:grid-cols-2 gap-6">
        
        {/* Licences Input */}
        <div>
          <label className="block text-[#111827] font-semibold mb-3 flex items-center gap-2">
            <Users className="w-5 h-5 text-[#92710A]" />
            Nombre de licences
          </label>
          <div className="flex items-center gap-3">
            <button
              type="button"
              onClick={decrementLicences}
              disabled={Licences <= 0 || Licences === ''}
              className="w-12 h-12 rounded-2xl bg-[#F8F7F4] hover:bg-[#F0EDE6] disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center text-[#92710A] transition-colors border border-[#E8E5DE] shadow-sm"
            >
              <Minus className="w-5 h-5" />
            </button>
            
            {/* 🔒 P1 MOA: Saisie libre - permet effacement complet */}
            <input
              type="number"
              min="0"
              max="10000"
              value={Licences}
              onChange={(e) => {
                const rawValue = e.target.value;
                // Permettre la saisie vide pour l'effacement
                if (rawValue === '') {
                  setLicences('');
                  return;
                }
                const val = parseInt(rawValue);
                if (!isNaN(val)) {
                  setLicences(Math.min(Math.max(val, 0), 10000));
                }
              }}
              placeholder="1"
              className="flex-1 bg-[#F8F7F4] border border-[#DDD9D0] rounded-xl px-4 py-3 text-center text-2xl font-bold text-[#111827] focus:outline-none focus:ring-2 focus:ring-[#D4AF37]"
            />
            
            <button
              type="button"
              onClick={incrementLicences}
              disabled={Licences >= 10000}
              className="w-12 h-12 rounded-2xl bg-[#F8F7F4] hover:bg-[#F0EDE6] disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center text-[#92710A] transition-colors border border-[#E8E5DE] shadow-sm"
            >
              <Plus className="w-5 h-5" />
            </button>
          </div>
          <p className="text-[#6B7280] text-sm mt-2">
            1 licence = 1 utilisateur activé
          </p>
        </div>
        
        {/* Duration Input */}
        <div>
          <label className="block text-[#111827] font-semibold mb-3 flex items-center gap-2">
            <Calendar className="w-5 h-5 text-[#92710A]" />
            Durée par licence (mois)
          </label>
          <div className="flex items-center gap-3">
            <button
              type="button"
              onClick={decrementDuration}
              disabled={durationMonths <= 0 || durationMonths === ''}
              className="w-12 h-12 rounded-2xl bg-[#F8F7F4] hover:bg-[#F0EDE6] disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center text-[#92710A] transition-colors border border-[#E8E5DE] shadow-sm"
            >
              <Minus className="w-5 h-5" />
            </button>
            
            {/* 🔒 P1 MOA: Saisie libre - permet effacement complet */}
            <input
              type="number"
              min="0"
              max="36"
              value={durationMonths}
              onChange={(e) => {
                const rawValue = e.target.value;
                // Permettre la saisie vide pour l'effacement
                if (rawValue === '') {
                  setDurationMonths('');
                  return;
                }
                const val = parseInt(rawValue);
                if (!isNaN(val)) {
                  setDurationMonths(Math.min(Math.max(val, 0), 36));
                }
              }}
              placeholder="1"
              className="flex-1 bg-[#F8F7F4] border border-[#DDD9D0] rounded-xl px-4 py-3 text-center text-2xl font-bold text-[#111827] focus:outline-none focus:ring-2 focus:ring-[#D4AF37]"
            />
            
            <button
              type="button"
              onClick={incrementDuration}
              disabled={durationMonths >= 36}
              className="w-12 h-12 rounded-2xl bg-[#F8F7F4] hover:bg-[#F0EDE6] disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center text-[#92710A] transition-colors border border-[#E8E5DE] shadow-sm"
            >
              <Plus className="w-5 h-5" />
            </button>
          </div>
          <p className="text-[#6B7280] text-sm mt-2">
            Durée d&apos;accès par utilisateur activé
          </p>
        </div>
      </div>
      
      {/* Pricing Display - AFFICHAGE STRICT BACKEND */}
      <div className="bg-[#F8F7F4] rounded-2xl p-6 border border-[#E8DFC8]">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <Calculator className="w-5 h-5 text-[#92710A]" />
            <span className="text-[#111827] font-semibold">Récapitulatif</span>
          </div>
          {loadingPricing && (
            <Loader2 className="w-5 h-5 text-[#92710A] animate-spin" />
          )}
        </div>
        
        {pricing ? (
          <div className="space-y-4">
            {/* Unit Price - FROM BACKEND (admin Tarification config) */}
            <div className="flex justify-between items-center text-[#4B5563]">
              <span>Prix unitaire mensuel</span>
              <span className="font-medium">
                {formatPrice(pricing.unit_price_monthly)} / licence / mois
              </span>
            </div>
            
            {/* Formula - FROM BACKEND */}
            <div className="flex justify-between items-center text-[#374151] text-sm">
              <span>Formule</span>
              <span className="font-mono bg-[#F5F0E3] px-2 py-1 rounded text-[#92710A] font-mono">
                {pricing.formula || `${Licences} × ${durationMonths} × ${pricing.unit_price_monthly}€`}
              </span>
            </div>
            
            {/* Divider */}
            <div className="border-t border-[#E8E5DE]"></div>
            
            {/* Total Price - FROM BACKEND */}
            <div className="flex justify-between items-center">
              <span className="text-[#111827] text-lg font-semibold">Prix total</span>
              <span className="text-3xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-[#D4AF37] to-[#92710A]">
                {formatPrice(pricing.total_price)}
              </span>
            </div>
            
            {/* Currency info */}
            <p className="text-[#4B5563] text-xs text-right">
              {pricing.currency?.toUpperCase() || 'EUR'} • TVA incluse
            </p>
          </div>
        ) : (
          <div className="text-center py-4">
            {loadingPricing ? (
              <p className="text-[#6B7280]">Calcul du prix en cours...</p>
            ) : (
              <p className="text-[#6B7280]">Configurez vos options pour voir le prix</p>
            )}
          </div>
        )}
      </div>
      
      {/* Info box */}
      <div className="bg-[#F5F0E3] border border-[#E8DFC8] rounded-xl p-4">
        <p className="text-[#7A5F0A] text-sm">
          💡 <strong>Licences non utilisées :</strong> Les licences non attribuées ne périment jamais. 
          Vous pouvez les activer pour vos collaborateurs à tout moment.
        </p>
      </div>
    </div>
  );
};

export default TokenCalculator;
